import React, { createContext, useContext, useState, useCallback } from 'react';

const MetricsContext = createContext();

export function useMetrics() {
  const context = useContext(MetricsContext);
  if (!context) {
    throw new Error('useMetrics must be used within a MetricsProvider');
  }
  return context;
}

const formatNumber = (num) => {
  if (num >= 1000000000) {
    return (num / 1000000000).toFixed(1) + 'b';
  }
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'm';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'k';
  }
  return num.toString();
};

export function MetricsProvider({ children }) {
  const [metrics, setMetrics] = useState({
    stats: [],
    loading: true,
    error: null
  });

  const getDateRange = (period) => {
    const now = new Date();
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    switch (period) {
      case 'today':
        return {
          start: startOfDay.getTime() / 1000,
          end: now.getTime() / 1000
        };
      case 'this_week':
        const startOfWeek = new Date(startOfDay);
        startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
        return {
          start: startOfWeek.getTime() / 1000,
          end: now.getTime() / 1000
        };
      case 'month_to_date':
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        return {
          start: startOfMonth.getTime() / 1000,
          end: now.getTime() / 1000
        };
      case 'year_to_date':
        const startOfYear = new Date(now.getFullYear(), 0, 1);
        return {
          start: startOfYear.getTime() / 1000,
          end: now.getTime() / 1000
        };
    }
  };

  const fetchMetrics = useCallback(async (period = 'this_week') => {
    try {
      setMetrics(prev => ({ ...prev, loading: true, error: null }));
      const { start, end } = getDateRange(period);
      
      const response = await fetch(`/api/metrics?start_date=${start}&end_date=${end}`, {
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch metrics');
      }

      const data = await response.json();
      
      // Transform the data into our stats format
      const transformedStats = [
        {
          title: "Total Videos",
          value: formatNumber(data.total_videos),
          change: `${data.total_videos_change >= 0 ? '+' : ''}${data.total_videos_change}%`,
          platform: "all"
        },
        {
          title: "Total Views",
          value: formatNumber(data.total_views),
          change: `${data.total_views_change >= 0 ? '+' : ''}${data.total_views_change}%`,
          platform: "all"
        }
      ];

      setMetrics({
        stats: transformedStats,
        loading: false,
        error: null,
        viewsChange: data.total_views_change,
        followersChange: data.total_followers_change,
        bestPostingTime: data.best_posting_time || '8PM',
        topPerformers: data.top_performers || [
          ["Most Viewed", {}],
          ["Most Engaged", {}]
        ]
      });
    } catch (error) {
      setMetrics(prev => ({
        ...prev,
        loading: false,
        error: error.message
      }));
    }
  }, []);

  return (
    <MetricsContext.Provider value={{ ...metrics, fetchMetrics }}>
      {children}
    </MetricsContext.Provider>
  );
} 